import { Component, inject, signal, OnInit, effect, computed, HostListener } from '@angular/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { I18nService } from '../../core/services/i18n.service';
import { MatButtonModule } from '@angular/material/button';
import { MatStepperModule } from '@angular/material/stepper';
import { filter } from 'rxjs/operators';
import { I18nTranslatePipe } from '../../core/pipes/i18n-translate.pipe';
import { Shared } from '../../core/services/shared';
import{ Faq } from '../../features/faq/faq';
import { PreferenceFormStore } from '../../core/stores/preference-form.store';
import { Images } from '../../core/constants/image.constants';
import { GlobalHeader } from '../../shared/global-header/global-header';

interface NavItem {
  icon: string;
  title: string;
  routerLink: string;
}

@Component({
  selector: 'app-navbar',
  imports: [
    CommonModule,
    MatToolbarModule,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    RouterModule,
    MatStepperModule,
    I18nTranslatePipe,
    Faq,
    GlobalHeader,
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
})
export class NavbarComponent implements OnInit {
  protected readonly shared = inject(Shared);
  private readonly preferenceFormStore = inject(PreferenceFormStore);

  protected readonly i18n = inject(I18nService);
  protected readonly router = inject(Router);

  images = Images;

  selectedIndex = computed(() => {
    const activeStep = this.preferenceFormStore.activeStep();
    const totalDependents = this.preferenceFormStore.totalDependents();
    if (this.isMotorPreferenceForm()) {
      // The store maps the last panel to the health Preferences step (3), and a step
      // left over from the health form can go past the end. Keep it within the three motor steps.
      return Math.min(activeStep, 2);
    }
    if (totalDependents === 0 && activeStep >= 2) {
      return 2;
    }
    return activeStep;
  });
  sidenavMode = signal<'side' | 'over'>('side');

  /** The motor form has its own stepper with three steps: Basic Info, Vehicle Info, Coverage Preferences. */
  protected readonly isMotorPreferenceForm = signal(false);

  userRole = signal('client');
  preferenceFormSteps = computed(() => {
    // The motor form has three fixed panels and no dependents step.
    if (this.isMotorPreferenceForm()) {
      return [
        { label: 'NAV.STEP_BASIC_INFO' },
        { label: 'NAV.STEP_VEHICLE_INFO' },
        { label: 'NAV.STEP_COVERAGE_PREFERENCES' },
      ];
    }

    const totalDependents = this.preferenceFormStore.totalDependents();
    const steps = [
      { label: 'NAV.STEP_BASIC_INFO' },
      { label: 'NAV.STEP_PRIMARY_APPLICANT' },
    ];
    if (totalDependents > 0) {
      steps.push({ label: 'NAV.STEP_DEPENDENTS' });
    }
    steps.push({ label: 'NAV.STEP_PREFERENCES' });
    return steps;
  });
  topNavItems = signal<NavItem[]>([]);
  bottomNavItems = signal<NavItem[]>([]);

  ngOnInit(): void {
    this.updateSelectedIndex();
    this.updateFormContext();
    this.checkScreenSize();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateSelectedIndex();
        this.updateFormContext();
      });
  }

  private updateFormContext(): void {
    this.isMotorPreferenceForm.set(
      this.router.url.split('?')[0].startsWith('/motor/preference-form'),
    );
  }

  @HostListener('window:resize')
  onResize() {
    this.checkScreenSize();
  }

  private checkScreenSize(): void {
    const isMobile = window.innerWidth <= 1024;
    this.sidenavMode.set(isMobile ? 'over' : 'side');
  }

  private updateSelectedIndex(): void {
    // Logic to update selected step index based on route if needed
  }

  toggleMenu(): void {
    this.shared.toggleSidebar();
  }

  onSidenavChange(isOpened: boolean) {
    this.shared.sidebarOpened.set(isOpened);
  }

  onStepChange(stepIndex: number) {
    // sync panel with step
    const totalDependents = this.preferenceFormStore.totalDependents();
    let panelIndex: number;

    if (totalDependents === 0) {
      switch (stepIndex) {
        case 0: // Basic
          panelIndex = 0;
          break;
        case 1: // Primary
          panelIndex = 1;
          break;
        case 2: // Preferences
          panelIndex = this.preferenceFormStore.lastPanelIndex();
          break;
        default:
          panelIndex = 0;
      }
    } else {
      switch (stepIndex) {
        case 0: // Basic
          panelIndex = 0;
          break;
        case 1: // Primary
          panelIndex = 1;
          break;
        case 2: // First dependent
          panelIndex = 2;
          break;
        case 3: // Preferences
          panelIndex = this.preferenceFormStore.lastPanelIndex();
          break;
        default:
          panelIndex = 0;
      }
    }

    this.preferenceFormStore.syncStepWithPanel(panelIndex);
  }

  stepStatus = computed(() => this.preferenceFormStore.stepStatus());

  isStepFinished(step: any, displayIndex: number): boolean {
    // The motor form marks each step by its panel index (0, 1 or 2), so no Preferences remap is needed.
    if (this.isMotorPreferenceForm()) {
      return this.stepStatus()[displayIndex] === 'complete';
    }
    const totalDependents = this.preferenceFormStore.totalDependents();
    let statusIndex = displayIndex;
    if (totalDependents === 0 && displayIndex === 2) statusIndex = 3; // Preferences index
    return this.stepStatus()[statusIndex] === 'complete';
  }
}
